import React, { useState, useEffect, useCallback, useRef } from 'react';
import { T, API_BASE } from '../constants';

// NUIT Monitor — Gold (GC1!) Overnight LONG. Polls
// /strategies/api/strategies/nuit/decisions/ every 20s. One eval per day at 13:30 ET
// (HOLD gate: close>SMA100 + rising), exit 08:00 ET next morning. −50bp stop (mandatory).
// No grade — flat 1 contract on HOLD nights. status ∈ {OPEN, OPEN_0800, STOP_-50, SKIP}.
// cert NUIT-GC-OVERNIGHT-LOCK-…16D8C109 · 626n / 61% WR / +169.4% / DD −2.63%.

const REFRESH_MS = 20000;
const NUIT_COLOR = '#eab308';   // gold — Gold (GC)

const STATUS_COLORS = {
  OPEN: '#3b82f6', OPEN_0800: T.bull, 'STOP_-50': T.bear, STOP: T.bear, SKIP: T.textMuted, EXIT: '#3b82f6',
};
const decisionColor = (d) => (d === 'TRADE' ? NUIT_COLOR : d === 'EXIT' ? '#3b82f6' : T.textMuted);

function fmtET(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
    const time = d.toLocaleTimeString('en-GB', { timeZone: 'America/New_York', hour12: false,
      hour: '2-digit', minute: '2-digit', second: '2-digit' });
    return `${date} ${time}`;
  } catch { return iso; }
}
const todayETStr = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
const num = (v, d = 2) => (v == null ? '—' : Number(v).toFixed(d));

function Pill({ value, color, mono = false }) {
  if (value == null || value === '') return <span style={{ color: T.textMuted }}>—</span>;
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 10,
      fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
      background: `${color || T.border}22`, border: `1px solid ${color || T.border}66`,
      color: color || T.text, fontFamily: mono ? 'Roboto Mono, monospace' : 'inherit' }}>{String(value)}</span>
  );
}
function Stat({ label, value, color }) {
  return (
    <div style={{ flex: '1 1 110px', padding: '12px 14px', borderRadius: 6,
      background: 'rgba(255,255,255,0.02)', border: `1px solid ${T.border}` }}>
      <div style={{ fontSize: 10, color: T.textMuted, fontWeight: 700, letterSpacing: '0.08em',
        textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 22, color: color || T.text, fontFamily: 'Roboto Mono, monospace', marginTop: 4 }}>{value}</div>
    </div>
  );
}

export default function NuitMonitor() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const [actionFilter, setActionFilter] = useState('');
  const [limit, setLimit] = useState(200);
  const [since, setSince] = useState('');
  const timerRef = useRef(null);

  const fetchRows = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      let url = `${API_BASE}/strategies/api/strategies/nuit/decisions/?limit=${limit}`;
      if (actionFilter) url += `&action=${actionFilter}`;
      if (since) url += `&since=${since}`;
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      setRows(j.decisions || []);
      setLastFetched(new Date());
    } catch (e) { setError(e.message || String(e)); }
    finally { setLoading(false); }
  }, [actionFilter, limit, since]);

  useEffect(() => {
    fetchRows();
    timerRef.current = setInterval(fetchRows, REFRESH_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [fetchRows]);

  // PnL on EXIT rows; sum EXIT only to avoid double-counting TRADE+EXIT.
  const c = rows.reduce((a, r) => {
    if (r.action === 'TAKE') { a.takes += 1; if (!r.exit_time) a.open += 1; }
    else if (r.action === 'EXIT') { a.exits += 1; if (r.pnl_bp != null) a.net += r.pnl_bp; if (r.stopped) a.stops += 1; }
    else a.skips += 1;
    return a;
  }, { takes: 0, open: 0, exits: 0, skips: 0, net: 0, stops: 0 });

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', background: T.bg }}>
    <div style={{ padding: 'clamp(12px, 3vw, 20px) clamp(10px, 4vw, 28px)', maxWidth: 1850, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap',
        paddingBottom: 14, borderBottom: `1px solid ${T.border}`, marginBottom: 18 }}>
        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: T.text }}>NUIT — Gold Overnight LONG (GC1!)</h1>
        <span style={{ fontSize: 11, color: T.textMuted }}>
          Overnight LONG · 13:30 ET eval (close&gt;SMA100 + rising) · exit 08:00 ET · −50bp stop (mandatory) ·
          no grade (flat 1c) · cert …16D8C109 · 626n / 61% WR / +169.4% / DD −2.63%
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center',
          flexWrap: 'wrap', justifyContent: 'flex-end', flex: '1 1 360px' }}>
          <label style={{ fontSize: 11, color: T.textMuted }}>Since</label>
          <input type="date" value={since} max={todayETStr()} onChange={e => setSince(e.target.value)}
            style={{ padding: '4px 8px', fontSize: 12, borderRadius: 4, border: `1px solid ${T.border}`,
              background: T.bg, color: T.text, fontFamily: 'Roboto Mono, monospace' }} />
          <button onClick={() => setSince('')} style={{ padding: '4px 8px', fontSize: 10, fontWeight: 700,
            letterSpacing: '0.05em', textTransform: 'uppercase', borderRadius: 4, cursor: 'pointer',
            border: `1px solid ${T.border}`, background: 'transparent', color: T.textMuted }}>Clear</button>
          <select value={actionFilter} onChange={e => setActionFilter(e.target.value)}
            style={{ padding: '4px 8px', fontSize: 11, borderRadius: 4, border: `1px solid ${T.border}`, background: T.bg, color: T.text }}>
            <option value="">All actions</option><option value="TAKE">TAKE</option>
            <option value="EXIT">EXIT</option><option value="SKIP">SKIP</option>
          </select>
          <select value={limit} onChange={e => setLimit(parseInt(e.target.value, 10))}
            style={{ padding: '4px 8px', fontSize: 11, borderRadius: 4, border: `1px solid ${T.border}`, background: T.bg, color: T.text }}>
            <option value={100}>100 rows</option><option value={200}>200 rows</option>
            <option value={500}>500 rows</option>
          </select>
          <button onClick={fetchRows} disabled={loading} style={{ padding: '5px 12px', fontSize: 11, fontWeight: 700,
            letterSpacing: '0.07em', textTransform: 'uppercase', borderRadius: 4, cursor: loading ? 'wait' : 'pointer',
            border: 'none', background: `${NUIT_COLOR}2e`, color: NUIT_COLOR }}>{loading ? 'Loading…' : 'Refresh'}</button>
        </div>
      </div>

      {error && (
        <div style={{ padding: 10, marginBottom: 12, borderRadius: 4, background: 'rgba(239,68,68,0.1)',
          border: `1px solid ${T.bear}`, color: T.bear, fontSize: 12 }}>Error: {error}</div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
        <Stat label="Rows" value={rows.length} color={T.text} />
        <Stat label="Trades" value={c.takes} color={NUIT_COLOR} />
        <Stat label="Open" value={c.open} color={STATUS_COLORS.OPEN} />
        <Stat label="Exits" value={c.exits} color={T.bull} />
        <Stat label="Stops" value={c.stops} color={T.bear} />
        <Stat label="SKIP" value={c.skips} color={T.textMuted} />
        <Stat label="Net bp" value={num(c.net, 0)} color={c.net >= 0 ? T.bull : T.bear} />
      </div>

      <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 8, fontFamily: 'Roboto Mono, monospace' }}>
        Overnight log · {rows.length} rows · newest first · 13:30 ET → exit 08:00 ET · return ≈ {num(c.net / 100, 1)}%
        {lastFetched && ` · updated ${lastFetched.toLocaleTimeString('en-GB', { hour12: false })} ET`}
        <span style={{ marginLeft: 10 }}>· exits: <span style={{ color: T.bull }}>08:00</span> ·{' '}
          <span style={{ color: T.bear }}>−50bp stop</span></span>
      </div>

      <div style={{ overflowX: 'auto', border: `1px solid ${T.border}`, borderRadius: 6, background: 'rgba(255,255,255,0.01)' }}>
        <table style={{ width: '100%', minWidth: 1600, borderCollapse: 'collapse', fontSize: 11, fontFamily: 'Roboto Mono, monospace' }}>
          <thead>
            <tr style={{ background: 'rgba(255,255,255,0.04)', textAlign: 'left', color: T.textMuted,
              fontSize: 10, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              {['Time (ET)', 'Session', 'Decision', 'Entry $', 'Stop $', 'pct_sma bp', 'SMA slope bp',
                'on_ret bp', 'Status', 'Exit (ET)', 'Exit $', 'PnL bp', 'Reason'].map((h, i) => (
                <th key={i} style={{ padding: '8px 10px', textAlign: i >= 3 && i <= 11 && i !== 8 && i !== 9 ? 'right' : 'left' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={13} style={{ padding: '20px 14px', color: T.textMuted, textAlign: 'center', fontStyle: 'italic' }}>
                No NUIT evals yet. Each day at 13:30 ET the HOLD gate decides (close&gt;SMA100 + rising);
                HOLD nights enter LONG and exit 08:00 ET. Auto-refresh {REFRESH_MS / 1000}s.
              </td></tr>
            )}
            {rows.map(r => {
              const st = (r.status || (r.action === 'TAKE' ? 'OPEN' : r.action));
              const stColor = STATUS_COLORS[st] || T.textMuted;
              const open = r.action === 'TAKE' && !r.exit_time;
              const isExit = r.action === 'EXIT';
              return (
                <tr key={r.id} style={{ borderTop: `1px solid ${T.border}`,
                  background: open ? 'rgba(59,130,246,0.06)' : isExit ? 'rgba(234,179,8,0.05)' : 'transparent' }}>
                  <td style={{ padding: '6px 10px', color: T.text }}>{fmtET(r.signal_ts)}</td>
                  <td style={{ padding: '6px 10px', color: T.textMuted }}>{r.session_date || '—'}</td>
                  <td style={{ padding: '6px 10px' }}><Pill value={r.decision} color={decisionColor(r.decision)} /></td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', color: T.text }}>{num(r.entry_price)}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', color: T.bear }}>{num(r.stop_level)}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', color: T.textMuted }}>{num(r.pct_above_bp, 0)}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right',
                    color: r.sma_slope_bp == null ? T.textMuted : r.sma_slope_bp >= 0 ? T.bull : T.bear }}>{num(r.sma_slope_bp, 1)}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right',
                    color: r.on_ret_bp == null ? T.textMuted : r.on_ret_bp >= 0 ? T.bull : T.bear }}>{num(r.on_ret_bp, 1)}</td>
                  <td style={{ padding: '6px 10px' }}><Pill value={st} color={stColor} mono /></td>
                  <td style={{ padding: '6px 10px', color: T.textMuted }}>{r.exit_time ? fmtET(r.exit_time) : '—'}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', color: T.text }}>{num(r.exit_price)}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 700,
                    color: r.pnl_bp == null ? T.textMuted : r.pnl_bp >= 0 ? T.bull : T.bear }}>
                    {r.pnl_bp != null ? num(r.pnl_bp, 1) : '—'}</td>
                  <td style={{ padding: '6px 10px', color: T.textMuted, fontSize: 10, maxWidth: 180,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.reason || ''}>{r.reason || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
    </div>
  );
}
